import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";

import {
  Document,
  Page,
  Image,
  Text,
  PDFViewer,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

import "../../barcodeform.css";
const JsBarcode = require("jsbarcode");

const DriverRunsheet = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(false);

  // UI Styles (separate from PDF styles)
  const uiStyles = {
    container: {
      padding: "24px",
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "16px",
    },
    titleSection: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    title: {
      fontSize: "22px",
      fontWeight: "600",
      color: "#2d3748",
      margin: 0,
    },
    subtitle: {
      fontSize: "13px",
      color: "#6b7280",
      marginTop: "4px",
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: "20px",
      border: "1px solid #e8ecf1",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      borderRadius: "12px 12px 0 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px",
    },
    cardTitle: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#374151",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    cardBody: {
      padding: "20px",
    },
    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "20px",
    },
    formGroup: {
      marginBottom: "0",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: "600",
      color: "#6b7280",
      marginBottom: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      transition: "all 0.2s",
      outline: "none",
      boxSizing: "border-box",
    },
    selectContainer: {
      width: "100%",
    },
    btnPrimary: {
      padding: "12px 28px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#fff",
      backgroundColor: "#1e3a5f",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: "0 4px 6px rgba(30, 58, 95, 0.3)",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 12px",
      fontSize: "12px",
      fontWeight: "500",
      borderRadius: "20px",
      gap: "6px",
    },
    badgeSuccess: {
      backgroundColor: "#dcfce7",
      color: "#16a34a",
    },
    badgeInfo: {
      backgroundColor: "#dbeafe",
      color: "#2563eb",
    },
    pdfContainer: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      border: "1px solid #e8ecf1",
      overflow: "hidden",
    },
    pdfHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px",
    },
    pdfViewer: {
      width: "100%",
      height: "calc(100vh - 350px)",
      minHeight: "500px",
      border: "none",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#6b7280",
    },
    emptyIcon: {
      fontSize: "48px",
      marginBottom: "16px",
      opacity: 0.5,
    },
    emptyText: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#4b5563",
      marginBottom: "8px",
    },
    emptySubtext: {
      fontSize: "14px",
      color: "#9ca3af",
    },
    errorText: {
      color: "#dc2626",
      fontSize: "14px",
      padding: "12px 16px",
      backgroundColor: "#fef2f2",
      borderRadius: "8px",
      marginTop: "16px",
    },
    statsRow: {
      display: "flex",
      gap: "16px",
      marginTop: "16px",
      flexWrap: "wrap",
    },
    statCard: {
      flex: "1",
      minWidth: "150px",
      padding: "16px",
      backgroundColor: "#f9fafb",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
    },
    statLabel: {
      fontSize: "12px",
      color: "#6b7280",
      marginBottom: "4px",
      textTransform: "uppercase",
    },
    statValue: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111827",
    },
  };

  // Custom select styles
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#1e3a5f" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(30, 58, 95, 0.1)" : "none",
      "&:hover": {
        borderColor: "#1e3a5f",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#1e3a5f" : state.isFocused ? "#e8eef5" : "#fff",
      color: state.isSelected ? "#fff" : "#374151",
      padding: "10px 14px",
      cursor: "pointer",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#374151",
    }),
  };

  const barcodeGenator = (id) => {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, id, {
      height: 50,
      width: 2,
      displayValue: true,
      fontSize: 45,
    });
    return canvas.toDataURL();
  };

  const barcodeGenator2 = (id) => {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, id, {
      height: 50,
      width: 2,
      displayValue: true,
    });
    return canvas.toDataURL();
  };

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split("T")[0];
  const [datechosen, setdatechosen] = useState(formattedDate);
  const [searchQuery, setSearchQuery] = useState("");
  const [scaninfo, setScaninfo] = useState({ homebase: "", driver: "" });
  const [drivers, setDrivers] = useState([]);
  const [msg, setMsg] = useState();
  const [driver, setdriver] = useState("");
  const [driverName, setdriverName] = useState("");
  const [runSheet, setRunSheet] = useState(null);
  const [error, setError] = useState(null);
  // "new" = the money-aware black & white layout; "classic" = the original runsheet, untouched.
  const [layout, setLayout] = useState("new");

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/driver/getAllDrivers`
      );
      if (response.data.success) {
        const driversData = response.data.data;
        const driversList = driversData.map((driver) => ({
          driver_id: driver.driverId,
          driver_name: driver.name,
        }));
        setDrivers(driversList);
      } else {
        console.error("Error fetching drivers for emirate:");
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const getRunsheet = async (driverId, driverName) => {
    setError("");
    setRunSheet(null);
    if (!datechosen) {
      setError("Please select a date first");
      setdriver("");
    } else {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}shypvdriverapp/driver/getDriverRunSheet`,
          {
            params: {
              driverId: driverId,
              date: datechosen,
            },
          }
        );
        const data = response.data.data;
        if (Array.isArray(data) && data.length > 0) {
          const allDetails = data.flatMap((runsheet) => runsheet.runsheetDetails);
          const uniqueDetailsMap = new Map();
          allDetails.forEach((detail) => {
            uniqueDetailsMap.set(detail.bookingId, detail);
          });
          const uniqueDetails = Array.from(uniqueDetailsMap.values());
          const cleanedRunsheet = [
            {
              runsheetId: `Combined-${driverId}-${datechosen}`,
              runsheetDetails: uniqueDetails,
            },
          ];
          setRunSheet(cleanedRunsheet);
        }
      } catch (err) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
      setdriver(driverId);
      setdriverName(driverName);
    }
  };

  // PDF Styles (for react-pdf)
  const styles = StyleSheet.create({
    page: {
      padding: 10,
      fontSize: 10,
      marginBottom: 5,
    },
    row: {
      flexDirection: "row",
      marginBottom: 5,
    },
    col: {
      flex: 2,
      padding: 5,
      borderBottomWidth: 1,
      borderBottomColor: "#000",
      borderBottomStyle: "solid",
      borderLeftWidth: 1,
      borderLeftColor: "#000",
      borderLeftStyle: "solid",
      textAlign: "center",
    },
    col0: {
      flex: 2,
      padding: 5,
      textAlign: "left",
    },
    col2: {
      flex: 2,
      padding: 5,
      borderRightWidth: 1,
      borderRightColor: "#000",
      borderRightStyle: "solid",
      textAlign: "center",
      maxHeight: 70,
    },
    col3: {
      flex: 2,
      borderRightWidth: 1,
      borderRightColor: "#000",
      borderRightStyle: "solid",
      textAlign: "center",
      maxHeight: 70,
    },
    col4: {
      flex: 5,
      padding: 5,
      borderRightWidth: 1,
      borderRightColor: "#000",
      borderRightStyle: "solid",
      textAlign: "center",
    },
    colh4: {
      flex: 5,
      padding: 5,
      borderBottomWidth: 1,
      borderBottomColor: "#000",
      borderBottomStyle: "solid",
      borderLeftWidth: 1,
      borderLeftColor: "#000",
      borderLeftStyle: "solid",
      textAlign: "center",
    },
    col6: {
      flex: 2,
      padding: 5,
      textAlign: "left",
    },
    col7: {
      flex: 2,
      padding: 5,
      textAlign: "center",
      maxHeight: 70,
    },
    tableHeader: {
      fontWeight: "bold",
      backgroundColor: "#f1f1f1",
      fontSize: 12,
    },
    table: {
      marginTop: 20,
      width: "100%",
      borderWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
    },
    image: {
      width: 150,
      padding: 3,
      alignSelf: "center",
      maxHeight: 200,
    },
    image2: {
      width: 150,
      padding: 3,
      alignSelf: "center",
      maxHeight: 200,
    },
    text: {
      marginBottom: 2,
    },
    dateText: {
      fontSize: 12,
      fontWeight: "bold",
    },
    contentText: {
      fontSize: 12,
    },
  });

  // ---------------------------------------------------------------------------
  // NEW RUNSHEET (black & white). Deliberately monochrome: no fills, no colour -
  // it is printed on a mono laser printer and carried in a van, so contrast comes
  // from rules and weight, never from shading.
  // ---------------------------------------------------------------------------
  const nsStyles = StyleSheet.create({
    page: { padding: 18, fontSize: 9, color: "#000" },
    titleRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end",
      borderBottomWidth: 2,
      borderBottomColor: "#000",
      paddingBottom: 6,
      marginBottom: 8,
    },
    title: { fontSize: 15, fontWeight: "bold" },
    metaRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 8 },
    metaCell: { flexDirection: "column" },
    metaLabel: { fontSize: 7, marginBottom: 1 },
    metaValue: { fontSize: 10, fontWeight: "bold" },
    kmBox: {
      flexDirection: "row",
      borderWidth: 1,
      borderColor: "#000",
      marginBottom: 10,
    },
    kmCell: {
      flex: 1,
      padding: 4,
      borderRightWidth: 1,
      borderRightColor: "#000",
    },
    kmCellLast: { flex: 1, padding: 4 },
    kmLabel: { fontSize: 7 },
    kmLine: { marginTop: 8, borderBottomWidth: 1, borderBottomColor: "#000" },

    table: { borderWidth: 1, borderColor: "#000" },
    headRow: {
      flexDirection: "row",
      borderBottomWidth: 2,
      borderBottomColor: "#000",
    },
    row: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#000",
    },
    cShip: { width: "19%", padding: 4, borderRightWidth: 1, borderRightColor: "#000" },
    cCons: { width: "23%", padding: 4, borderRightWidth: 1, borderRightColor: "#000" },
    cMeta: { width: "10%", padding: 4, borderRightWidth: 1, borderRightColor: "#000" },
    cCod:  { width: "11%", padding: 4, borderRightWidth: 1, borderRightColor: "#000" },
    cChg:  { width: "15%", padding: 4, borderRightWidth: 1, borderRightColor: "#000" },
    cSum:  { width: "14%", padding: 4, borderRightWidth: 1, borderRightColor: "#000" },
    cSign: { width: "8%", padding: 4 },
    codTag: { fontSize: 7, fontWeight: "bold", marginBottom: 2 },
    amtBig: { fontSize: 11, fontWeight: "bold" },
    dash: { fontSize: 9 },
    right: { textAlign: "right" },
    hText: { fontSize: 8, fontWeight: "bold" },
    bold: { fontWeight: "bold" },
    small: { fontSize: 7 },
    barcode: { width: 95, marginBottom: 2 },

    money: { flexDirection: "row", justifyContent: "space-between" },
    moneyRule: { borderBottomWidth: 1, borderBottomColor: "#000", marginVertical: 2 },
    collect: { flexDirection: "row", justifyContent: "space-between", marginTop: 1 },

    totalsBox: {
      marginTop: 10,
      borderWidth: 2,
      borderColor: "#000",
      padding: 6,
    },
    totalsRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 2 },
    grand: {
      flexDirection: "row",
      justifyContent: "space-between",
      borderTopWidth: 1,
      borderTopColor: "#000",
      paddingTop: 3,
      marginTop: 2,
    },
    footNote: { marginTop: 10, fontSize: 7 },
    signStrip: { flexDirection: "row", marginTop: 14 },
    signCell: { flex: 1, marginRight: 14 },
    signLine: { borderBottomWidth: 1, borderBottomColor: "#000", height: 22 },
  });

  const money = (n) => (Number(n) || 0).toFixed(2);

  // The backend resolves currency from duties_tax.currency and falls back to AED.
  const rowCurrency = (detail) => (detail && detail.currency) || "AED";

  const sheetCurrency = (() => {
    const withCur =
      runSheet &&
      runSheet
        .flatMap((rs) => rs.runsheetDetails || [])
        .find((d) => d && d.currency);
    return withCur ? withCur.currency : "AED";
  })();

  // Goods value == COD amount; charges are the uploaded duties/tax + operational add-ons.
  const allDetails = runSheet
    ? runSheet.flatMap((rs) => rs.runsheetDetails || [])
    : [];
  const totalGoods = allDetails.reduce(
    (a, d) => a + (parseFloat(d.codAmount) || 0),
    0
  );
  const totalCharges = allDetails.reduce(
    (a, d) => a + (parseFloat(d.chargesTotal) || 0),
    0
  );
  const totalCollect = allDetails.reduce(
    (a, d) =>
      a +
      (d.totalCollect != null
        ? parseFloat(d.totalCollect) || 0
        : (parseFloat(d.codAmount) || 0) + (parseFloat(d.chargesTotal) || 0)),
    0
  );

  const totalCodAmount =
    runSheet &&
    runSheet.reduce((sum, runsheet) => {
      const detailTotal = runsheet.runsheetDetails?.reduce(
        (detailSum, detail) => detailSum + (parseFloat(detail.codAmount) || 0),
        0
      );
      return sum + detailTotal;
    }, 0);

  // Match on BOTH id and name: the option label is "<id> - <name>", so filtering by name
  // alone made typing a driver id return "No drivers found" even though the id is on screen.
  // Guarded against rows with a null name so one bad record can't blank the whole dropdown.
  const filteredDrivers = Array.isArray(drivers)
    ? drivers.filter((driver) => {
        const q = (searchQuery || "").trim().toLowerCase();
        if (!q) return true;
        const name = String(driver.driver_name || "").toLowerCase();
        const id = String(driver.driver_id || "").toLowerCase();
        return name.includes(q) || id.includes(q);
      })
    : [];

  const driverOptions = filteredDrivers.map((driver) => ({
    value: driver.driver_id,
    label: `${driver.driver_id} - ${driver.driver_name}`,
  }));

  const handleChange = (selectedOption) => {
    const selectedDriverId = selectedOption.value;
    const selectedDriverName = selectedOption.label;
    setdriver(selectedDriverId);
    setdriverName(selectedDriverName);
    setRunSheet(null); // Clear previous runsheet when driver changes
    setError(null);
  };

  const handleRequestRunsheet = () => {
    if (driver && driverName) {
      getRunsheet(driver, driverName);
    } else {
      setError("Please select a driver first");
    }
  };

  const totalOrders = runSheet
    ? runSheet.reduce((sum, rs) => sum + (rs.runsheetDetails?.length || 0), 0)
    : 0;

  return (
    <div style={uiStyles.container}>
      {/* Header Section */}
      <div style={uiStyles.header}>
        <div style={uiStyles.titleSection}>
          <div>
            <h1 style={uiStyles.title}>Driver Runsheet</h1>
            <p style={uiStyles.subtitle}>
              View and print driver delivery runsheets
            </p>
          </div>
        </div>
        {runSheet && (
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <span style={{ ...uiStyles.badge, ...uiStyles.badgeSuccess }}>
              {totalOrders} Orders
            </span>
            <span style={{ ...uiStyles.badge, ...uiStyles.badgeInfo }}>
              Goods: {sheetCurrency} {money(totalGoods)}
            </span>
            <span style={{ ...uiStyles.badge, ...uiStyles.badgeInfo }}>
              Charges: {sheetCurrency} {money(totalCharges)}
            </span>
            <span
              style={{
                ...uiStyles.badge,
                backgroundColor: "#1e3a5f",
                color: "#fff",
              }}
            >
              To Collect: {sheetCurrency} {money(totalCollect)}
            </span>
          </div>
        )}
      </div>

      {/* Filter Card */}
      <div style={uiStyles.card}>
        <div style={uiStyles.cardHeader}>
          <h3 style={uiStyles.cardTitle}>
            Select Driver & Date
          </h3>
        </div>
        <div style={uiStyles.cardBody}>
          <div style={uiStyles.formRow}>
            <div style={uiStyles.formGroup}>
              <label style={uiStyles.label}>Date *</label>
              <input
                type="date"
                style={uiStyles.input}
                value={datechosen}
                onChange={(e) => {
                  setdatechosen(e.target.value);
                  setRunSheet(null); // Clear previous runsheet when date changes
                  setError(null);
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#1e3a5f";
                  e.target.style.boxShadow = "0 0 0 3px rgba(30, 58, 95, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
            <div style={uiStyles.formGroup}>
              <label style={uiStyles.label}>Select Driver *</label>
              <Select
                options={driverOptions}
                value={driverOptions.find(
                  (option) => option.value === driver
                )}
                onChange={handleChange}
                placeholder="Search and select a driver..."
                onInputChange={(newValue) => setSearchQuery(newValue)}
                isSearchable
                noOptionsMessage={() => "No drivers found"}
                styles={customSelectStyles}
              />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
            <button
              style={{
                ...uiStyles.btnPrimary,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
                transform: "scale(1)",
              }}
              onClick={handleRequestRunsheet}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = "#2c4a6e";
                  e.target.style.transform = "scale(1.02)";
                  e.target.style.boxShadow = "0 6px 12px rgba(30, 58, 95, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#1e3a5f";
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 4px 6px rgba(30, 58, 95, 0.3)";
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.target.style.transform = "scale(0.98)";
                }
              }}
              onMouseUp={(e) => {
                if (!loading) {
                  e.target.style.transform = "scale(1.02)";
                }
              }}
            >
              {loading ? "Loading..." : "Request Runsheet"}
            </button>
          </div>
          {error && (
            <div style={uiStyles.errorText}>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer Section */}
      {loading ? (
        <div style={uiStyles.card}>
          <div style={uiStyles.emptyState}>
            <div style={uiStyles.emptyIcon}>...</div>
            <p style={uiStyles.emptyText}>Loading runsheet...</p>
            <p style={uiStyles.emptySubtext}>Please wait while we fetch the data</p>
          </div>
        </div>
      ) : runSheet && runSheet.length > 0 ? (
        <div style={uiStyles.pdfContainer}>
          <div style={uiStyles.pdfHeader}>
            <h3 style={uiStyles.cardTitle}>
              Runsheet Preview - {driverName}
            </h3>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              {/* Layout switch: New is the money-aware B&W sheet, Classic is the original. */}
              <div
                style={{
                  display: "inline-flex",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {[
                  { key: "new", label: "New Runsheet" },
                  { key: "classic", label: "Classic" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setLayout(opt.key)}
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: "600",
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: layout === opt.key ? "#1e3a5f" : "#fff",
                      color: layout === opt.key ? "#fff" : "#6b7280",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span style={{ ...uiStyles.badge, ...uiStyles.badgeInfo }}>
                {datechosen}
              </span>
            </div>
          </div>

          {/* On-screen runsheet: the same numbers the PDF prints, readable without
              opening the print preview. Mirrors the New layout's money model. */}
          <div style={{ padding: "0 20px 20px" }}>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#fafbfc" }}>
                    {["AWB / Ref", "Consignee", "Wt", "COD", "Extra Charge", "Summary"].map(
                      (h, i) => (
                        <th
                          key={h}
                          style={{
                            textAlign: i >= 3 ? "right" : "left",
                            padding: "10px 12px",
                            borderBottom: "2px solid #e8ecf1",
                            color: "#6b7280",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: "0.3px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {runSheet
                    .flatMap((rs) => rs.runsheetDetails || [])
                    .map((d, i) => {
                      const goods = parseFloat(d.codAmount) || 0;
                      const chg = parseFloat(d.chargesTotal) || 0;
                      const collect =
                        d.totalCollect != null
                          ? parseFloat(d.totalCollect) || 0
                          : goods + chg;
                      const cur = rowCurrency(d);
                      const isCod =
                        String(d.paymentType || "").toUpperCase().includes("COD") && goods > 0;
                      const num = {
                        textAlign: "right",
                        padding: "10px 12px",
                        borderBottom: "1px solid #eef1f5",
                        whiteSpace: "nowrap",
                      };
                      const cell = {
                        padding: "10px 12px",
                        borderBottom: "1px solid #eef1f5",
                        verticalAlign: "top",
                      };
                      return (
                        <tr key={i}>
                          <td style={cell}>
                            <div style={{ fontWeight: 600 }}>{d.bookingId}</div>
                            <div style={{ color: "#6b7280", fontSize: "11px" }}>
                              Ref: {d.businessRefNum || "-"}
                            </div>
                          </td>
                          <td style={cell}>
                            <div>{d.consignee?.name || "-"}</div>
                            <div style={{ color: "#6b7280", fontSize: "11px" }}>
                              {d.consignee?.phone || ""}
                            </div>
                          </td>
                          <td style={cell}>{d.weight || "-"}</td>
                          <td style={num}>
                            {isCod ? (
                              <>
                                <div style={{ fontSize: "10px", color: "#6b7280", fontWeight: 600 }}>
                                  COD
                                </div>
                                <div style={{ fontWeight: 700 }}>{money(goods)}</div>
                              </>
                            ) : (
                              <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600 }}>
                                NON-COD
                              </div>
                            )}
                          </td>
                          <td style={num}>
                            {(d.charges || []).length === 0 ? (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            ) : (
                              <div>
                                {d.charges.map((c, ci) => (
                                  <div key={ci} style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {c.name}: {money(c.amount)}
                                  </div>
                                ))}
                                <div
                                  style={{
                                    fontWeight: 700,
                                    marginTop: "2px",
                                    borderTop: "1px solid #e8ecf1",
                                    paddingTop: "2px",
                                  }}
                                >
                                  {money(chg)}
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={num}>
                            <div style={{ fontSize: "11px", color: "#6b7280" }}>
                              COD {isCod ? money(goods) : "—"}
                            </div>
                            <div style={{ fontSize: "11px", color: "#6b7280" }}>
                              Charges {chg ? money(chg) : "—"}
                            </div>
                            {collect > 0 ? (
                              <div style={{ fontWeight: 700, marginTop: "2px" }}>
                                {cur} {money(collect)}
                              </div>
                            ) : (
                              <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600 }}>
                                NOTHING TO COLLECT
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: "#fafbfc" }}>
                    <td colSpan={3} style={{ padding: "12px", fontWeight: 700 }}>
                      Totals ({totalOrders} shipments)
                    </td>
                    <td style={{ padding: "12px", textAlign: "right", fontWeight: 700 }}>
                      {money(totalGoods)}
                    </td>
                    <td style={{ padding: "12px", textAlign: "right", fontWeight: 700 }}>
                      {money(totalCharges)}
                    </td>
                    <td style={{ padding: "12px", textAlign: "right", fontWeight: 700 }}>
                      {sheetCurrency} {money(totalCollect)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <PDFViewer style={uiStyles.pdfViewer}>
            {layout === "new" ? (
              /* ---------- NEW RUNSHEET: black & white, money-aware ---------- */
              <Document>
                {runSheet.map((runsheet, rsIndex) => (
                  <Page key={rsIndex} size="A4" style={nsStyles.page}>
                    <View style={nsStyles.titleRow}>
                      <Text style={nsStyles.title}>DRIVER RUNSHEET</Text>
                      <Text style={nsStyles.small}>
                        Runsheet {runsheet.runsheetId} | Page {rsIndex + 1} of {runSheet.length}
                      </Text>
                    </View>

                    <View style={nsStyles.metaRow}>
                      <View style={nsStyles.metaCell}>
                        <Text style={nsStyles.metaLabel}>COURIER</Text>
                        <Text style={nsStyles.metaValue}>{driverName}</Text>
                      </View>
                      <View style={nsStyles.metaCell}>
                        <Text style={nsStyles.metaLabel}>DATE</Text>
                        <Text style={nsStyles.metaValue}>{datechosen}</Text>
                      </View>
                      <View style={nsStyles.metaCell}>
                        <Text style={nsStyles.metaLabel}>SHIPMENTS</Text>
                        <Text style={nsStyles.metaValue}>
                          {runsheet.runsheetDetails?.length || 0}
                        </Text>
                      </View>
                      <View style={nsStyles.metaCell}>
                        <Text style={nsStyles.metaLabel}>TO COLLECT</Text>
                        <Text style={nsStyles.metaValue}>
                          {sheetCurrency} {money(totalCollect)}
                        </Text>
                      </View>
                    </View>

                    <View style={nsStyles.kmBox}>
                      <View style={nsStyles.kmCell}>
                        <Text style={nsStyles.kmLabel}>VEHICLE NO.</Text>
                        <View style={nsStyles.kmLine} />
                      </View>
                      <View style={nsStyles.kmCell}>
                        <Text style={nsStyles.kmLabel}>OPENING KM</Text>
                        <View style={nsStyles.kmLine} />
                      </View>
                      <View style={nsStyles.kmCell}>
                        <Text style={nsStyles.kmLabel}>CLOSING KM</Text>
                        <View style={nsStyles.kmLine} />
                      </View>
                      <View style={nsStyles.kmCellLast}>
                        <Text style={nsStyles.kmLabel}>TOTAL KM</Text>
                        <View style={nsStyles.kmLine} />
                      </View>
                    </View>

                    <View style={nsStyles.table}>
                      <View style={nsStyles.headRow}>
                        <View style={nsStyles.cShip}><Text style={nsStyles.hText}>AWB / SHIPPER</Text></View>
                        <View style={nsStyles.cCons}><Text style={nsStyles.hText}>CONSIGNEE</Text></View>
                        <View style={nsStyles.cMeta}><Text style={nsStyles.hText}>TYPE / WT</Text></View>
                        <View style={nsStyles.cCod}><Text style={nsStyles.hText}>COD</Text></View>
                        <View style={nsStyles.cChg}><Text style={nsStyles.hText}>EXTRA CHARGE</Text></View>
                        <View style={nsStyles.cSum}><Text style={nsStyles.hText}>SUMMARY</Text></View>
                        <View style={nsStyles.cSign}><Text style={nsStyles.hText}>RECEIVED</Text></View>
                      </View>

                      {runsheet.runsheetDetails?.map((detail, i) => {
                        const goods = parseFloat(detail.codAmount) || 0;
                        const charges = detail.charges || [];
                        const chargesTotal = parseFloat(detail.chargesTotal) || 0;
                        const collect =
                          detail.totalCollect != null
                            ? parseFloat(detail.totalCollect) || 0
                            : goods + chargesTotal;
                        const cur = rowCurrency(detail);
                        // Treat it as COD only if the shipment says so AND there is an amount.
                        const isCod =
                          String(detail.paymentType || "").toUpperCase().includes("COD") &&
                          goods > 0;
                        return (
                          <View style={nsStyles.row} key={i} wrap={false}>
                            <View style={nsStyles.cShip}>
                              <Image
                                style={nsStyles.barcode}
                                src={
                                  detail.bookingId
                                    ? barcodeGenator2(detail.bookingId)
                                    : barcodeGenator2(detail.businessRefNum)
                                }
                              />
                              <Text style={nsStyles.small}>Ref: {detail.businessRefNum}</Text>
                              <Text style={nsStyles.small}>{detail.shipper?.name}</Text>
                            </View>

                            <View style={nsStyles.cCons}>
                              <Text style={nsStyles.bold}>{detail.consignee?.name}</Text>
                              <Text style={nsStyles.small}>{detail.consignee?.address}</Text>
                              <Text style={nsStyles.small}>{detail.consignee?.phone}</Text>
                            </View>

                            <View style={nsStyles.cMeta}>
                              <Text style={nsStyles.small}>Wt: {detail.weight}</Text>
                              <Text style={nsStyles.small}>Items: {detail.noOfItems}</Text>
                            </View>

                            {/* COD - its own column. A non-COD shipment says so in words rather
                                than printing 0.00, so the driver never has to infer it. */}
                            <View style={nsStyles.cCod}>
                              {isCod ? (
                                <View>
                                  <Text style={nsStyles.codTag}>COD</Text>
                                  <Text style={nsStyles.amtBig}>{money(goods)}</Text>
                                </View>
                              ) : (
                                <View>
                                  <Text style={nsStyles.codTag}>NON-COD</Text>
                                  <Text style={nsStyles.dash}>--</Text>
                                </View>
                              )}
                            </View>

                            {/* Extra charges - its own column, itemised then totalled. Payable
                                even on a NON-COD shipment, which is why it is kept separate. */}
                            <View style={nsStyles.cChg}>
                              {charges.length === 0 ? (
                                <Text style={nsStyles.dash}>--</Text>
                              ) : (
                                <View>
                                  {charges.map((c, ci) => (
                                    <View style={nsStyles.money} key={ci}>
                                      <Text style={nsStyles.small}>{c.name}</Text>
                                      <Text style={nsStyles.small}>{money(c.amount)}</Text>
                                    </View>
                                  ))}
                                  <View style={nsStyles.moneyRule} />
                                  <View style={nsStyles.money}>
                                    <Text style={nsStyles.small}>Total</Text>
                                    <Text style={nsStyles.bold}>{money(chargesTotal)}</Text>
                                  </View>
                                </View>
                              )}
                            </View>

                            {/* Summary - what the driver actually collects at the door. */}
                            <View style={nsStyles.cSum}>
                              <View style={nsStyles.money}>
                                <Text style={nsStyles.small}>COD</Text>
                                <Text style={nsStyles.small}>{isCod ? money(goods) : "--"}</Text>
                              </View>
                              <View style={nsStyles.money}>
                                <Text style={nsStyles.small}>Charges</Text>
                                <Text style={nsStyles.small}>
                                  {charges.length ? money(chargesTotal) : "--"}
                                </Text>
                              </View>
                              <View style={nsStyles.moneyRule} />
                              {collect > 0 ? (
                                <View>
                                  <Text style={nsStyles.codTag}>COLLECT {cur}</Text>
                                  <Text style={nsStyles.amtBig}>{money(collect)}</Text>
                                </View>
                              ) : (
                                <Text style={nsStyles.codTag}>NOTHING TO COLLECT</Text>
                              )}
                            </View>

                            <View style={nsStyles.cSign} />
                          </View>
                        );
                      })}
                    </View>

                    {rsIndex === runSheet.length - 1 && (
                      <View style={nsStyles.totalsBox}>
                        <View style={nsStyles.totalsRow}>
                          <Text>Total goods value (COD)</Text>
                          <Text>{sheetCurrency} {money(totalGoods)}</Text>
                        </View>
                        <View style={nsStyles.totalsRow}>
                          <Text>Total extra charges (duties, tax, add-ons)</Text>
                          <Text>{sheetCurrency} {money(totalCharges)}</Text>
                        </View>
                        <View style={nsStyles.grand}>
                          <Text style={nsStyles.bold}>TOTAL TO COLLECT</Text>
                          <Text style={nsStyles.bold}>
                            {sheetCurrency} {money(totalCollect)}
                          </Text>
                        </View>
                      </View>
                    )}

                    {rsIndex === runSheet.length - 1 && (
                      <View style={nsStyles.signStrip}>
                        <View style={nsStyles.signCell}>
                          <View style={nsStyles.signLine} />
                          <Text style={nsStyles.small}>Driver signature</Text>
                        </View>
                        <View style={nsStyles.signCell}>
                          <View style={nsStyles.signLine} />
                          <Text style={nsStyles.small}>Cash handed over / received by</Text>
                        </View>
                      </View>
                    )}

                    <Text style={nsStyles.footNote}>
                      Goods value is the COD amount on the shipment. Extra charges are the
                      duties, tax and operational add-ons uploaded for this AWB.
                    </Text>
                  </Page>
                ))}
              </Document>
            ) : (

            <Document>
              {runSheet.map((runsheet, runsheetIndex) => (
                <Page key={runsheetIndex} size="A4" style={styles.page}>
                  {/* Header Section */}
                  <View style={styles.row} wrap={false}>
                    <View style={styles.col6}>
                      <Text style={styles.dateText}>Date: {datechosen}</Text>
                      <Text style={styles.dateText}>Courier: {driverName}</Text>
                    </View>
                    <View style={styles.col6}>
                      <Image
                        style={styles.image2}
                        src={barcodeGenator(`Runsheet: ${runsheet.runsheetId}`)}
                      />
                    </View>
                    <View style={styles.col0}>
                      <Text style={styles.contentText}>Vehicle No:</Text>
                      <Text style={styles.contentText}>Opening KM:</Text>
                      <Text style={styles.contentText}>Closing KM:</Text>
                    </View>
                  </View>

                  {/* Table Section */}
                  <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.row}>
                      <View style={[styles.colh4, styles.tableHeader]}>
                        <Text>Barcode</Text>
                      </View>
                      <View style={[styles.col, styles.tableHeader]}>
                        <Text>Shipper</Text>
                      </View>
                      <View style={[styles.colh4, styles.tableHeader]}>
                        <Text>Consignee</Text>
                      </View>
                      <View style={[styles.col, styles.tableHeader]}>
                        <Text>Weight</Text>
                      </View>
                      <View style={[styles.col, styles.tableHeader]}>
                        <Text>Payment Type</Text>
                      </View>
                      <View style={[styles.col, styles.tableHeader]}>
                        <Text>Price</Text>
                      </View>
                    </View>

                    {/* Table Rows */}
                    {runsheet.runsheetDetails.map((detail, detailIndex) => (
                      <View style={styles.row} key={detailIndex} wrap={false}>
                        <View style={styles.col4}>
                          <Image
                            style={styles.image}
                            src={
                              detail.bookingId
                                ? barcodeGenator2(detail.bookingId)
                                : barcodeGenator2(detail.businessRefNum)
                            }
                          />
                          <Text style={{ marginTop: 10 }}>
                            Ref: {detail.businessRefNum}
                          </Text>
                        </View>
                        <View style={styles.col2}>
                          <Text>{detail.shipper.name}</Text>
                        </View>
                        <View style={styles.col4}>
                          <Text>{detail.consignee.name}</Text>
                          <Text>{detail.consignee.address}</Text>
                          <Text>{detail.consignee.phone}</Text>
                        </View>
                        <View style={styles.col2}>
                          <Text>{detail.weight}</Text>
                        </View>
                        <View style={styles.col2}>
                          <Text>{detail.paymentType}</Text>
                        </View>
                        <View style={styles.col6}>
                          <Text>{detail.codAmount ? detail.codAmount : "0"}</Text>
                        </View>
                      </View>
                    ))}
                  </View>
                  {runsheetIndex === runSheet.length - 1 && (
                    <View style={{ marginTop: 20, padding: 10 }}>
                      <Text style={{ fontSize: 14, fontWeight: "bold" }}>
                        Total COD Amount: {sheetCurrency} {totalCodAmount.toFixed(2)}
                      </Text>
                    </View>
                  )}
                </Page>
              ))}
            </Document>
            )}
          </PDFViewer>
        </div>
      ) : (
        <div style={uiStyles.card}>
          <div style={uiStyles.emptyState}>
            <div style={{ ...uiStyles.emptyIcon, fontSize: "32px", color: "#9ca3af" }}>—</div>
            <p style={uiStyles.emptyText}>
              {driver ? "No runsheet available for this driver" : "No runsheet selected"}
            </p>
            <p style={uiStyles.emptySubtext}>
              {driver
                ? "Try selecting a different date"
                : "Select a date and driver to view the runsheet"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverRunsheet;
